/**
 * MobileShell — wraps an active screen with the bottom tab bar + FAB.
 *
 * The screen content is given the entire viewport; the tab bar and FAB
 * are fixed-position children. Children are responsible for their own
 * scroll containers; we just leave bottom padding so content isn't
 * hidden behind the chrome.
 */
import type { ReactNode } from 'react';

import FAB from './FAB';
import InstallPrompt from '@/components/common/InstallPrompt';
import TabBar, { type MobileTabKey } from './TabBar';
import BackToTop from './BackToTop';

export interface MobileShellProps {
  /** null = neither tab is active (deep route like /settings, /recur, ...).
   *  In that case any tap delegates to onTab so the user can leave the
   *  deep page via 帳本 / 分析. */
  active: MobileTabKey | null;
  onTab: (k: MobileTabKey) => void;
  onAdd: () => void;
  children: ReactNode;
  /** Hide chrome (tab bar + FAB) when an overlay/sheet takes the whole screen. */
  hideChrome?: boolean;
  /** True when the current route is the home screen — used to suppress
   *  the floating back-to-top button (home uses tap-same-tab instead so
   *  there's only one affordance for "scroll to top"). */
  onHome?: boolean;
}

export default function MobileShell({
  active,
  onTab,
  onAdd,
  children,
  hideChrome = false,
  onHome = false,
}: MobileShellProps): JSX.Element {
  // Tapping the same tab the user is already on scrolls the document
  // back to the top — feels native, mirrors iOS / Android conventions
  // for tab bars. For different tabs, we delegate to the parent's
  // navigation handler.
  const onTabIntercept = (k: MobileTabKey): void => {
    if (k === active) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    onTab(k);
  };

  return (
    <div
      style={{
        minHeight: '100dvh',
        background: 'var(--bg-app)',
        position: 'relative',
        // Reserve space for tab bar + FAB so screen content isn't covered.
        paddingBottom: hideChrome ? 0 : 'calc(env(safe-area-inset-bottom, 0px) + 90px)',
      }}
    >
      {children}
      {!hideChrome && (
        <>
          <TabBar active={active} onChange={onTabIntercept} />
          <FAB onClick={onAdd} />
          <InstallPrompt />
          {/* Floating back-to-top — appears once scrolled past 200px on
              every screen EXCEPT home (home uses tap-same-tab instead,
              avoiding two affordances for the same action). */}
          <BackToTop enabled={!onHome} />
        </>
      )}
    </div>
  );
}
